import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GlobalService } from '../global.service';
import { AjaxService } from '../ajax.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class I18nService {
    public availableLanguages = [
        { code: 'nl', name: 'Nederlands', sttcode: 'nl-NL' },
        { code: 'en', name: 'English', sttcode: 'en-US' },
        { code: 'de', name: 'Deutsch', sttcode: 'de-DE' },
        { code: 'fr', name: 'Français', sttcode: 'fr-FR' },
        { code: 'lt', name: 'Latviešu', sttcode: 'lv-LV' },
        { code: 'pl', name: 'Polski', sttcode: 'pl-PL' },
    ];

    private translations: { [key: string]: string } = {};
    private loadedLanguage: string = '';
    private isLoading: boolean = false;
    public translationsLoaded$ = new BehaviorSubject<boolean>(false);

    constructor(
        private http: HttpClient,
        private global: GlobalService,
        private ajax: AjaxService
    ) {
        // Load translations for the current language
        this.loadTranslations(this.global.language);
    }

    getSTTlanguage(language: string): string {
        const result = this.availableLanguages.find(
            ({ code }) => code === language
        );

        return result.sttcode;
    }

    /**
     * Synchronous translation function - translations are preloaded
     */
    tr(txt: string): string {
        if (txt === null || txt === undefined) {
            return '';
        }

        // Check if we need to load translations for a different language
        if (this.loadedLanguage !== this.global.language && !this.isLoading) {
            this.loadTranslations(this.global.language);
            return txt; // Return original text while loading
        }

        // Return translation if available, otherwise return original text
        return this.translations[txt] || txt;
    }

    /**
     * Preload translations for a specific language
     */
    loadTranslations(language: string): void {
        if (this.isLoading || this.loadedLanguage === language) {
            return; // Already loading or already loaded
        }

        this.isLoading = true;
        this.translations = {};

        this.http
            .get<{ [key: string]: string }>(`assets/i18n/${language}.json`)
            .subscribe({
                next: (translations) => {
                    this.translations = translations;
                    this.loadedLanguage = language;
                    this.isLoading = false;
                    this.translationsLoaded$.next(true);
                    console.log(
                        `Translations loaded for language: ${language}`
                    );
                },
                error: (error) => {
                    console.error(
                        `Could not load translations for language: ${language}`,
                        error
                    );
                    this.loadedLanguage = language; // Mark as loaded to prevent retries
                    this.isLoading = false;
                    this.translationsLoaded$.next(false);
                },
            });
    }

    /**
     * Change language and preload translations
     */
    setLanguage(language: string): void {
        if (this.global.language !== language) {
            this.global.language = language;
            this.loadTranslations(language);
        }
        this.ajax
            .get('setLang', {
                lang: this.global.language,
            })
            .then((data: any) => {
                console.log('setLang response:', data);
            });
    }

    /**
     * Observable to know when translations are loaded
     */
    get translationsReady$(): Observable<boolean> {
        return this.translationsLoaded$.asObservable();
    }

    /**
     * Check if translations are currently loaded for the active language
     */
    get isReady(): boolean {
        return this.loadedLanguage === this.global.language && !this.isLoading;
    }
}
